import React, { lazy, Suspense, useEffect } from 'react';
import BkTabs from '../../components/tabs/BkTabs';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import BkNoticeFormButtons from './step-pages/BkNoticeFormButtons';
import BkProgressBar from '../../components/progress-bar/BkProgressBar';
import { cancelCreation, clearSubmitJobError } from '../../features/upload/uploadSlice';
import { useNavigate } from 'react-router-dom';
import { reset } from '../../features/stepper/stepperSlice';
import { DirectMailingType, NoticeType } from '../../utils/constant';
import { TABS } from './utilities/utils';
import BkLoader from '../../components/loader/BkLoader';
import CancelModalManager from './step-pages/components/cancel-modal/CancelModalWrapper';
import { Typography } from 'src/components/new/Typography';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import LogoComponent from 'src/components/logo/LogoComponent';
import { Box } from '@mui/material';
import { useFormStepNavigation } from './hooks/useFormNavigation';

const PreferencesPage = lazy(() => import('./step-pages/PreferencesPage'));
const CourtCasePage = lazy(() => import('./step-pages/CourtCasePage'));
const DocumentsPage = lazy(() => import('./step-pages/DocumentsPage'));
const AddressPage = lazy(() => import('./step-pages/AddressPage'));
const ReviewAndSubmitPage = lazy(() => import('./step-pages/ReviewAndSubmitPage'));

const BkNoticeForm: React.FC = () => {
  const directMailingType = useSelector((state: RootState) => state.upload.formData.directMailingType);

  const noticeType = useSelector((state: RootState) => state.upload.noticeType);

  const isCreatingNotice = useSelector((state: RootState) => state.upload.isCreatingNotice);

  const submitJobError = useSelector((state: RootState) => state.upload.submitJobError);
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const { currentIndex } = useFormStepNavigation()

  type LazyComponentProps = {
    Component: React.LazyExoticComponent<React.FC>;
  };

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (localStorage.getItem('token')) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const LazyComponentWithProps: React.FC<LazyComponentProps> = React.memo(({ Component }) => {
    return <Component />;
  });

  const getFormHeader = (noticeType: NoticeType) => {
    switch (noticeType.type) {
      case 'legal':
        return 'Legal Mailing';
      case 'notice-bk':
        return 'Bankruptcy Notice';
      case 'direct':
        return 'Direct Mailing';
    }
  };

  const getStepsForNoticeType = (type: NoticeType['type'], currentIndex: number) => {
    const componentImports: { [key: string]: React.LazyExoticComponent<any> } = {
      CourtCasePage: CourtCasePage,
      DocumentsPage: DocumentsPage,
      AddressPage: AddressPage,
      PreferencesPage: PreferencesPage,
      ReviewAndSubmitPage: ReviewAndSubmitPage,
    };

    let steps = TABS[type];

    if (type === 'direct') {
      steps = steps.filter((step) => {
        if (directMailingType?.type === 'direct_mail_personalized' && step.componentName === 'AddressPage') {
          return false; // Omit "Select Addresses" for personalized
        }
        return true;
      });
    }

    return steps.map((tab: { label: string; componentName: string }) => () => (
      <LazyComponentWithProps key={tab.componentName} Component={componentImports[tab.componentName]} />
    ));
  };

  const getStepperData = (type: NoticeType['type'], directMailingType: DirectMailingType | null) => {
    let steps = TABS[type];

    if (directMailingType === null) {
      return steps;
    }

    if (type === 'direct') {
      steps = steps.filter((step) => {
        if (directMailingType?.type === 'direct_mail_personalized' && step.componentName === 'AddressPage') {
          return false; // Omit "Select Addresses" for personalized
        }
        return true;
      });
    }

    return steps;
  };

  const steps = getStepsForNoticeType(noticeType.type, currentIndex);

  const stepperData = getStepperData(noticeType.type, directMailingType);

  const CurrentStepComponent = steps[currentIndex];

  useEffect(() => {
    if (isCreatingNotice?.status === 'success') {
      const navigateTo = noticeType.type;
      navigate('/success', { state: { navigateTo: navigateTo } });
      setTimeout(() => {
        dispatch(cancelCreation());
        dispatch(reset());
      }, 250);
    }

    if (isCreatingNotice?.status === 'error') {
    }
  }, [isCreatingNotice]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <div
        style={{
          position: 'relative',
          flex: 1,
        }}
      >
        <BkSnackBar
          open={!!submitJobError}
          snackMessage={submitJobError}
          handleCloseSnackbar={() => {
            dispatch(clearSubmitJobError());
          }}
        />
        <BkProgressBar activeIndex={currentIndex} totalSteps={stepperData.length} />
        <Box sx={{ position: 'fixed' }}>
          <LogoComponent height="1.875rem" />
        </Box>
        <div style={{ display: 'flex', padding: '1.875rem' }}>
          <div
            style={{
              position: 'fixed',
            }}
          >
            <Typography variant="h5" sx={{ marginBottom: '0.9375rem', fontWeight: '500', marginTop: '4.375rem' }}>
              {getFormHeader(noticeType)}
            </Typography>
            <BkTabs currentTabIndex={currentIndex} tabs={stepperData} />
          </div>
          <Suspense fallback={<div style={{ marginLeft: '15.625rem' }}>Loading...</div>}>
            <div style={{ flex: '2', marginLeft: '18.75rem', marginRight: '0.3125rem' }}>
              {CurrentStepComponent && <CurrentStepComponent />}
            </div>
            <CancelModalManager />
          </Suspense>
        </div>
        {isCreatingNotice?.status === 'pending' && (
          <BkLoader open={isCreatingNotice?.status === 'pending'} loaderTitle="Creating notice. Please wait..." />
        )}
      </div>
      <BkNoticeFormButtons shouldNavigateToSuccess={isCreatingNotice?.status === 'success'} />
    </div>
  );
};

export default BkNoticeForm;
